const POLL_STATUS_RETRY_BACKOFF_MS = [2000, 4000, 8000, 15000];
const POLL_STATUS_MAX_RETRY_DELAY_MS = 15000;
const SAFE_DIAGNOSTIC_ID_PATTERN = /^[A-Za-z0-9._:-]{1,200}$/;

const getSafeDiagnosticId = (value) => {
    if (value === undefined || value === null || value === "") return "unknown";

    const stringValue = String(value);
    return SAFE_DIAGNOSTIC_ID_PATTERN.test(stringValue) ? stringValue : "invalid";
};

const getSafeRequestHost = (requestUrl) => {
    if (!requestUrl) return "unknown";

    try {
        return new URL(String(requestUrl)).host || "unknown";
    } catch {
        // sentry-ignore: unparsable URL only affects a diagnostic tag
        return "invalid";
    }
};

const getSafeResponseStatus = (value) => {
    const numericValue = Number(value);
    return Number.isInteger(numericValue) && numericValue >= 100 && numericValue <= 599
        ? numericValue
        : "none";
};

export const getPreviewFailureMetricAttributes = (diagnostics = {}) => ({
    job_id: getSafeDiagnosticId(diagnostics.jobId),
    response_status: getSafeResponseStatus(diagnostics.httpStatus),
    failed_host: getSafeRequestHost(diagnostics.requestUrl),
});

export const getAxiosHttpStatus = (error) => error?.response?.status || null;

export const getAxiosCode = (error) => error?.code || null;

export const isPollStatusRequestError = (error) => Boolean(
    error?.isAxiosError ||
    error?.response ||
    error?.request ||
    error?.code === "ERR_NETWORK" ||
    error?.code === "ECONNABORTED" ||
    error?.code === "ETIMEDOUT"
);

export const isRetryablePollStatusError = (error) => {
    if (!isPollStatusRequestError(error)) return false;

    const status = getAxiosHttpStatus(error);
    if (!status) {
        return error?.code !== "ERR_CANCELED";
    }

    return status === 408 || status === 429 || status >= 500;
};

export const getPollStatusRetryDelayMs = (failureCount) => (
    POLL_STATUS_RETRY_BACKOFF_MS[failureCount] || POLL_STATUS_MAX_RETRY_DELAY_MS
);

export const getPollElapsedMs = (pollJob, now = Date.now()) => {
    const createdAt = Number(pollJob?.createdAt);
    if (!Number.isFinite(createdAt)) return null;

    return Math.max(0, now - createdAt);
};

export const getPollRemainingMs = (pollJob, timeoutMs, now = Date.now()) => {
    const numericTimeout = Number(timeoutMs);
    if (!Number.isFinite(numericTimeout) || numericTimeout <= 0) {
        return Infinity;
    }

    const elapsedMs = getPollElapsedMs(pollJob, now);
    if (elapsedMs === null) return numericTimeout;

    return Math.max(0, numericTimeout - elapsedMs);
};

export const getBoundedPollDelayMs = (pollJob, timeoutMs, delayMs, now = Date.now()) => {
    const numericDelay = Number(delayMs);
    if (!Number.isFinite(numericDelay) || numericDelay <= 0) return 0;

    return Math.max(0, Math.min(numericDelay, getPollRemainingMs(pollJob, timeoutMs, now)));
};

export const attachPollErrorDiagnostics = (error, diagnostics = {}) => {
    if (!error || typeof error !== "object") return error;

    error.pollDiagnostics = {
        ...(error.pollDiagnostics || {}),
        ...diagnostics,
    };

    if (diagnostics.failureReason) {
        error.previewFailureReason = diagnostics.failureReason;
    }

    return error;
};

export const getPollErrorDiagnostics = (error) => error?.pollDiagnostics || {};

export const getPreviewRequestDiagnostics = (
    error,
    { requestMode = "fetch", requestUrl = "" } = {}
) => {
    const pollDiagnostics = getPollErrorDiagnostics(error);
    const fallbackRequestUrl = requestMode === "fetch" ? requestUrl : "";

    return {
        ...pollDiagnostics,
        requestUrl: pollDiagnostics.requestUrl || error?.config?.url || fallbackRequestUrl,
        httpStatus: pollDiagnostics.httpStatus ?? getAxiosHttpStatus(error),
        axiosCode: pollDiagnostics.axiosCode || getAxiosCode(error),
    };
};

export const getPreviewFailureReason = (error, fallback = "request_failed") => (
    error?.previewFailureReason || fallback
);

export const getPollStatusRequestDescriptor = (statusUrl, jobId) => {
    const trimmedStatusUrl = String(statusUrl || "").trim();
    const encodedJobId = encodeURIComponent(jobId);

    if (trimmedStatusUrl.includes("{jobId}") || trimmedStatusUrl.includes(":jobId")) {
        return {
            method: "get",
            url: trimmedStatusUrl
                .split("{jobId}").join(encodedJobId)
                .split(":jobId").join(encodedJobId),
            body: null,
        };
    }

    return {
        method: "post",
        url: trimmedStatusUrl,
        body: { jobId },
    };
};

export async function runPreviewStatusPolling({
    initialPollJob,
    requestBody,
    initialIntervalMs,
    pollTimeoutMs,
    waitForPollInterval,
    assertPollActive,
    isPollJobExpired,
    clearStoredPollJob,
    persistPollJob,
    normalizePollIntervalMs,
    getStatusRequestDescriptor,
    requestStatus,
    getStatusPayload,
    evaluateStatusPayload,
    buildDiagnostics,
    enrichError = attachPollErrorDiagnostics,
    isCancelledError = () => false,
    now = () => Date.now(),
}) {
    let pollJob = initialPollJob;
    let intervalMs = normalizePollIntervalMs(pollJob.pollIntervalMs, initialIntervalMs);
    let lastHealthyIntervalMs = intervalMs;
    let transientStatusFailures = 0;

    const applyEvaluation = (evaluation) => {
        if (evaluation.done) {
            return { done: true };
        }

        pollJob = {
            ...evaluation.pollJob,
            jobId: evaluation.pollJob.jobId || pollJob.jobId,
        };
        intervalMs = evaluation.intervalMs;
        lastHealthyIntervalMs = intervalMs;
        transientStatusFailures = 0;

        return { done: false };
    };

    const runStatusCheck = async ({ finalCheck = false } = {}) => {
        const descriptor = getStatusRequestDescriptor(pollJob.jobId);
        const response = await requestStatus(descriptor);
        const statusPayload = getStatusPayload(response);
        const fallbackIntervalMs = transientStatusFailures > 0
            ? lastHealthyIntervalMs
            : intervalMs;
        const diagnostics = buildDiagnostics({
            pollJob,
            descriptor,
            response,
            pollPhase: "status",
            finalCheck,
        });
        const evaluation = evaluateStatusPayload(
            statusPayload,
            pollJob.storageKey,
            requestBody,
            fallbackIntervalMs,
            true,
            diagnostics
        );

        return applyEvaluation(evaluation);
    };

    // A page that was in the background (iOS pauses timers) can wake up past the
    // timeout while the job has long finished, so ask once more before giving up.
    const finishExpiredJob = async () => {
        try {
            const finalResult = await runStatusCheck({ finalCheck: true });
            if (finalResult.done) return;
        } catch (error) {
            if (isCancelledError(error) || !isPollStatusRequestError(error)) {
                throw error;
            }
        }

        clearStoredPollJob(pollJob.storageKey);
        const error = new Error("Polling-Job ist abgelaufen.");
        throw enrichError(error, buildDiagnostics({
            pollJob,
            descriptor: getStatusRequestDescriptor(pollJob.jobId),
            pollPhase: "status",
            failureReason: "poll_timeout",
            finalCheck: true,
        }));
    };

    let shouldPoll = true;

    while (shouldPoll) {
        assertPollActive();

        if (isPollJobExpired(pollJob)) {
            return finishExpiredJob();
        }

        await waitForPollInterval(
            getBoundedPollDelayMs(pollJob, pollTimeoutMs, intervalMs, now())
        );
        assertPollActive();

        if (isPollJobExpired(pollJob)) {
            return finishExpiredJob();
        }

        try {
            const result = await runStatusCheck();
            if (result.done) return;
        } catch (error) {
            if (isCancelledError(error)) {
                throw error;
            }

            const descriptor = getStatusRequestDescriptor(pollJob.jobId);
            const diagnostics = {
                ...buildDiagnostics({
                    error,
                    pollJob,
                    descriptor,
                    pollPhase: "status",
                    failureReason: getPreviewFailureReason(error),
                }),
                ...getPollErrorDiagnostics(error),
            };

            if (isRetryablePollStatusError(error) && !isPollJobExpired(pollJob)) {
                const retryDelayMs = getBoundedPollDelayMs(
                    pollJob,
                    pollTimeoutMs,
                    getPollStatusRetryDelayMs(transientStatusFailures),
                    now()
                );

                if (retryDelayMs > 0) {
                    transientStatusFailures += 1;
                    pollJob = persistPollJob(pollJob.storageKey, requestBody, {
                        ...pollJob,
                        status: pollJob.status || "processing",
                        pollIntervalMs: retryDelayMs,
                        lastStatusFailureAt: now(),
                        lastStatusHttpStatus: getAxiosHttpStatus(error),
                        lastStatusAxiosCode: getAxiosCode(error),
                    });
                    intervalMs = retryDelayMs;
                    continue;
                }
            }

            if (isPollStatusRequestError(error)) {
                try {
                    const finalResult = await runStatusCheck({ finalCheck: true });
                    if (finalResult.done) return;
                    continue;
                } catch (finalError) {
                    throw enrichError(finalError, {
                        ...buildDiagnostics({
                            error: finalError,
                            pollJob,
                            descriptor,
                            pollPhase: "status",
                            finalCheck: true,
                            failureReason: getPreviewFailureReason(finalError),
                        }),
                        ...getPollErrorDiagnostics(finalError),
                    });
                }
            }

            throw enrichError(error, diagnostics);
        }
    }
}
